import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'he' | 'en';

interface Translations {
  // Navigation
  dashboard: string;
  sessions: string;
  assessments: string;
  organizations: string;
  reports: string;
  users: string;
  settings: string;
  testMode: string;
  auditLogs: string;
  
  // Common Actions
  search: string;
  filter: string;
  export: string;
  import: string;
  create: string;
  edit: string;
  delete: string;
  save: string;
  cancel: string;
  confirm: string;
  refresh: string;
  download: string;
  send: string;
  
  // Session Management
  completedSessions: string;
  participantName: string;
  participantEmail: string;
  organization: string;
  position: string;
  startedAt: string;
  completedAt: string;
  score: string;
  status: string;
  viewDetails: string;
  generateChart: string;
  sendResults: string;
  exportToCSV: string;
  exportToExcel: string;
  
  // Assessment Management
  assessmentTitle: string;
  assessmentType: string;
  language: string;
  questions: string;
  isActive: string;
  createdAt: string;
  updatedAt: string;
  initializeAssessments: string;
  createAssessment: string;
  toggleStatus: string;
  preview: string;
  
  // Organization Management
  organizationName: string;
  contactEmail: string;
  totalSessions: string;
  lastActivity: string;
  generateReport: string;
  
  // Reports
  dateRange: string;
  from: string;
  to: string;
  averageScore: string;
  categoryScores: string;
  participants: string;
  completionTime: string;
  scoreDistribution: string;
  includeCharts: string;
  
  // Data Management
  dataRetention: string;
  retentionDays: string;
  cleanupOldData: string;
  dryRun: string;
  deleteTestSessions: string;
  
  // Messages
  loading: string;
  noData: string;
  error: string;
  success: string;
  confirmDelete: string;
  confirmCleanup: string;
  emailSent: string;
  exportComplete: string;
  
  // User Management
  userName: string;
  userEmail: string;
  role: string;
  lastLogin: string;
  addUser: string;
  editUser: string;
  admin: string;
  viewer: string;
  analyst: string;
  
  // Admin Page Specific
  'admin.dashboard': string;
  'admin.subtitle': string;
  'admin.loading': string;
  'admin.error': string;
  'admin.filter.organization': string;
  'admin.filter.all.organizations': string;
  'admin.filter.showing': string;
  'admin.filter.sessions': string;
  'admin.total.sessions': string;
  'admin.epoc.assessments': string;
  'admin.completed.sessions': string;
  'admin.participant': string;
  'admin.assessment': string;
  'admin.organization': string;
  'admin.completed': string;
  'admin.actions': string;
  'admin.no.sessions': string;
  'admin.view.details': string;
  'admin.session.details': string;
  'admin.participant.info': string;
  'admin.name': string;
  'admin.email': string;
  'admin.phone': string;
  'admin.position': string;
  'admin.address': string;
  'admin.employeeCount': string;
  'admin.assessment.results': string;
  'admin.generating.chart': string;
  'admin.chart.unavailable': string;
  'admin.print': string;
  'admin.printfull': string;
  'admin.close': string;
  'admin.comparison.title': string;
  'admin.comparison.subtitle': string;
  'admin.comparison.select.all': string;
  'admin.comparison.generate': string;
  'admin.comparison.clear': string;
  'admin.comparison.participants': string;
  'admin.comparison.generating': string;
  'admin.comparison.unavailable': string;

  // Session Filter
  'admin.filter.completed': string;
  'admin.filter.unfinished': string;
  'admin.filter.leads': string;
  'admin.unfinished.sessions': string;

  // Quiz Leads
  'admin.quiz.leads': string;
  'admin.lead.challenge': string;
  'admin.lead.urgency': string;
  'admin.lead.status': string;
  'admin.lead.wants360': string;
  'admin.lead.date': string;
  'admin.no.leads': string;
  'admin.lead.details': string;
  'admin.lead.answers': string;
  'admin.progress': string;
  'admin.status.started': string;
  'admin.status.in_progress': string;

  // Edit Participant Dialog
  'edit.participant.dialog.title': string;
  'edit.participant.dialog.name': string;
  'edit.participant.dialog.email': string;
  'edit.participant.dialog.organization': string;
  'edit.participant.dialog.position': string;
  'edit.participant.dialog.phone': string;
  'edit.participant.dialog.address': string;
  'edit.participant.dialog.employeeCount': string;
  'edit.participant.dialog.cancel': string;
  'edit.participant.dialog.update': string;
  'edit.participant.dialog.updating': string;
  'edit.participant.success': string;
  'edit.participant.error': string;
  
  // Delete Session
  'delete.session.title': string;
  'delete.session.message': string;
  'delete.session.warning': string;
  'delete.session.cancel': string;
  'delete.session.delete': string;
  'delete.session.deleting': string;
  'delete.session.success': string;
  'delete.session.error': string;
  
  // Bulk Delete
  'bulk.delete.title': string;
  'bulk.delete.message': string;
  'bulk.delete.warning': string;
  'bulk.delete.cancel': string;
  'bulk.delete.delete': string;
  'bulk.delete.deleting': string;
  'bulk.delete.button': string;
  'bulk.delete.success': string;
  'bulk.delete.error': string;
  
  // Tooltips
  'tooltip.view.details': string;
  'tooltip.edit.participant': string;
  'tooltip.delete.session': string;
  
  // Epok Analysis UI Texts
  epokAnalysisUI: {
    title: string;
    detailedAnalysisTitle: string;
    resultsChart: string;
    subtitle: string;
    strengths: string;
    development: string;
    improvement: string;
    dataNotAvailable: string;
    dataUnavailableMessage: string;
    participantDefault: string;
    growthPotential: string;
    businessGrowth: string;
    organizationalKnowledge: string;
    growthMessage: string;
    cashFlow: string;
    improvementNeeded: string;
    emergencyMode: string;
    constantState: string;
    unhealthyStress: string;
    harmingPerformance: string;
    topStrength: string;
    topWeakness: string;
    mainSymptoms: string;
    timeToRealize: string;
    nowYouKnow: string;
    changeStarts: string;
    keyToSuccess: string;
    categoriesBreakdown: string;
    currentState: string;
    summary: string;
    analysisConclusion: string;
    problemIdentification: string;
    analysisBasedOn: string;
    worstArea: string;
    mainProblemSource: string;
    improvementImpact: string;
    customSolution: string;
    mainRecommendations: string;
    keyMessage: string;
    problemNotCapital: string;
    problemIsKnowledge: string;
    solutionFormula: string;
    analysisCreated: string;
    methodology: string;
    copyright: string;
    criticalBusinessWarning: string;
    defaultScore: string;
    fallbackCategory: string;
  };
  
  // Comment Templates for Detailed Analysis
  commentTemplates: {
    positive: string[];
    improvement: string[];
    neutral: string[];
  };
  
  // Epok Recommendations
  epokRecommendations: {
    excellent: string[];
    good: string[];
    average: string[];
    poor: string[];
  };
  
  // Category Names
  categoryNames: {
    management: string;
    organization: string;
    repeatSales: string;
    finance: string;
    operations: string;
    qualityControl: string;
    newSales: string;
    stress: string;
    principles: string;
    helpAcceptance: string;
  };
  
  // General Category Descriptions
  categoryDescriptions: {
    management: string;
    organization: string;
    repeatSales: string;
    finance: string;
    operations: string;
    qualityControl: string;
    newSales: string;
    stress: string;
    principles: string;
    helpAcceptance: string;
  };
  
  // Epok Analysis Category Texts
  epokCategories: {
    management: {
      excellent: string;
      good: string;
      average: string;
      poor: string;
    };
    organization: {
      excellent: string;
      good: string;
      average: string;
      poor: string;
    };
    repeatSales: {
      excellent: string;
      good: string;
      average: string;
      poor: string;
    };
    finance: {
      excellent: string;
      good: string;
      average: string;
      poor: string;
    };
    operations: {
      excellent: string;
      good: string;
      average: string;
      poor: string;
    };
    qualityControl: {
      excellent: string;
      good: string;
      average: string;
      poor: string;
    };
    newSales: {
      excellent: string;
      good: string;
      average: string;
      poor: string;
    };
    stress: {
      excellent: string;
      good: string;
      average: string;
      poor: string;
    };
    principles: {
      excellent: string;
      good: string;
      average: string;
      poor: string;
    };
    helpAcceptance: {
      excellent: string;
      good: string;
      average: string;
      poor: string;
    };
  };
}

const translations: Record<Language, Translations> = {
  en: {
    // Navigation
    dashboard: 'Dashboard',
    sessions: 'Sessions',
    assessments: 'Assessments',
    organizations: 'Organizations',
    reports: 'Reports',
    users: 'Users',
    settings: 'Settings',
    testMode: 'Test Mode',
    auditLogs: 'Audit Logs',
    
    // Common Actions
    search: 'Search',
    filter: 'Filter',
    export: 'Export',
    import: 'Import',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    refresh: 'Refresh',
    download: 'Download',
    send: 'Send',
    
    // Session Management
    completedSessions: 'Completed Sessions',
    participantName: 'Participant Name',
    participantEmail: 'Email',
    organization: 'Organization',
    position: 'Position',
    startedAt: 'Started At',
    completedAt: 'Completed At',
    score: 'Score',
    status: 'Status',
    viewDetails: 'View Details',
    generateChart: 'Generate Chart',
    sendResults: 'Send Results',
    exportToCSV: 'Export to CSV',
    exportToExcel: 'Export to Excel',
    
    // Assessment Management
    assessmentTitle: 'Assessment Title',
    assessmentType: 'Type',
    language: 'Language',
    questions: 'Questions',
    isActive: 'Active',
    createdAt: 'Created At',
    updatedAt: 'Updated At',
    initializeAssessments: 'Initialize Assessments',
    createAssessment: 'Create Assessment',
    toggleStatus: 'Toggle Status',
    preview: 'Preview',
    
    // Organization Management
    organizationName: 'Organization Name',
    contactEmail: 'Contact Email',
    totalSessions: 'Total Sessions',
    lastActivity: 'Last Activity',
    generateReport: 'Generate Report',
    
    // Reports
    dateRange: 'Date Range',
    from: 'From',
    to: 'To',
    averageScore: 'Average Score',
    categoryScores: 'Category Scores',
    participants: 'Participants',
    completionTime: 'Completion Time',
    scoreDistribution: 'Score Distribution',
    includeCharts: 'Include Charts',
    
    // Data Management
    dataRetention: 'Data Retention',
    retentionDays: 'Retention Days',
    cleanupOldData: 'Cleanup Old Data',
    dryRun: 'Dry Run',
    deleteTestSessions: 'Delete Test Sessions',
    
    // Messages
    loading: 'Loading...',
    noData: 'No data available',
    error: 'An error occurred',
    success: 'Operation successful',
    confirmDelete: 'Are you sure you want to delete?',
    confirmCleanup: 'Are you sure you want to cleanup old data?',
    emailSent: 'Email sent successfully',
    exportComplete: 'Export completed',
    
    // User Management
    userName: 'User Name',
    userEmail: 'User Email',
    role: 'Role',
    lastLogin: 'Last Login',
    addUser: 'Add User',
    editUser: 'Edit User',
    admin: 'Admin',
    viewer: 'Viewer',
    analyst: 'Analyst',
    
    // Admin Page Specific
    'admin.dashboard': 'Assessment Dashboard',
    'admin.subtitle': 'View and manage completed assessment sessions',
    'admin.loading': 'Loading sessions...',
    'admin.error': 'Failed to load sessions',
    'admin.filter.organization': 'Filter by Organization',
    'admin.filter.all.organizations': 'All Organizations',
    'admin.filter.showing': 'Showing',
    'admin.filter.sessions': 'sessions',
    'admin.total.sessions': 'Total Sessions',
    'admin.epoc.assessments': '360 Assessments',
    'admin.completed.sessions': 'Completed Sessions',
    'admin.participant': 'Participant',
    'admin.assessment': 'Assessment',
    'admin.organization': 'Organization',
    'admin.completed': 'Completed',
    'admin.actions': 'Actions',
    'admin.no.sessions': 'No completed sessions found',
    'admin.view.details': 'View Details',
    'admin.session.details': 'Session Details',
    'admin.participant.info': 'Participant Information',
    'admin.name': 'Name',
    'admin.email': 'Email',
    'admin.phone': 'Phone',
    'admin.position': 'Position',
    'admin.address': 'Address',
    'admin.employeeCount': 'Employee Count',
    'admin.assessment.results': 'Assessment Results',
    'admin.generating.chart': 'Generating chart...',
    'admin.chart.unavailable': 'Chart unavailable',
    'admin.print': ' Print ',
    'admin.printfull': ' Print Full Report ',
    'admin.close': 'Close',
    'admin.comparison.title': 'Bulk Actions',
    'admin.comparison.subtitle': 'Select multiple participants to compare their assessment results',
    'admin.comparison.select.all': 'Select All',
    'admin.comparison.generate': 'Generate Comparison',
    'admin.comparison.clear': 'Clear Selection',
    'admin.comparison.participants': 'participants',
    'admin.comparison.generating': 'Generating comparison...',
    'admin.comparison.unavailable': 'Comparison chart unavailable',

    // Session Filter
    'admin.filter.completed': 'Completed',
    'admin.filter.unfinished': 'Unfinished',
    'admin.filter.leads': 'Leads',
    'admin.unfinished.sessions': 'Unfinished Sessions',

    // Quiz Leads
    'admin.quiz.leads': 'Quiz Leads',
    'admin.lead.challenge': 'Challenge',
    'admin.lead.urgency': 'Urgency',
    'admin.lead.status': 'Status',
    'admin.lead.wants360': '360?',
    'admin.lead.date': 'Date',
    'admin.no.leads': 'No leads yet',
    'admin.lead.details': 'Lead Details',
    'admin.lead.answers': 'Quiz Answers',
    'admin.progress': 'Status',
    'admin.status.started': 'Started',
    'admin.status.in_progress': 'In Progress',

    // Edit Participant Dialog
    'edit.participant.dialog.title': 'Edit Participant Details',
    'edit.participant.dialog.name': 'Name',
    'edit.participant.dialog.email': 'Email',
    'edit.participant.dialog.organization': 'Organization',
    'edit.participant.dialog.position': 'Position',
    'edit.participant.dialog.phone': 'Phone',
    'edit.participant.dialog.address': 'Address',
    'edit.participant.dialog.employeeCount': 'Employee Count',
    'edit.participant.dialog.cancel': 'Cancel',
    'edit.participant.dialog.update': 'Update',
    'edit.participant.dialog.updating': 'Updating...',
    'edit.participant.success': 'Participant details updated successfully',
    'edit.participant.error': 'Failed to update participant details',
    
    // Delete Session
    'delete.session.title': 'Confirm Delete',
    'delete.session.message': 'Are you sure you want to delete the session for',
    'delete.session.warning': 'This action cannot be undone. All session data and responses will be permanently deleted.',
    'delete.session.cancel': 'Cancel',
    'delete.session.delete': 'Delete',
    'delete.session.deleting': 'Deleting...',
    'delete.session.success': 'Successfully deleted session for',
    'delete.session.error': 'Failed to delete session',
    
    // Bulk Delete
    'bulk.delete.title': 'Confirm Bulk Delete',
    'bulk.delete.message': 'Are you sure you want to delete',
    'bulk.delete.warning': 'This action cannot be undone. All selected sessions and their data will be permanently deleted.',
    'bulk.delete.cancel': 'Cancel',
    'bulk.delete.delete': 'Delete',
    'bulk.delete.deleting': 'Deleting...',
    'bulk.delete.button': 'Delete Selected',
    'bulk.delete.success': 'Successfully deleted',
    'bulk.delete.error': 'Failed to delete sessions',
    
    // Tooltips
    'tooltip.view.details': 'View Details',
    'tooltip.edit.participant': 'Edit Participant',
    'tooltip.delete.session': 'Delete Session',
    
    // Epok Analysis UI Texts
    epokAnalysisUI: {
      title: 'Comprehensive analysis based questionnaire',
      detailedAnalysisTitle: 'Detailed Analysis',
      resultsChart: 'Results Chart',
      subtitle: '',
      strengths: 'Strengths',
      development: 'Development',
      improvement: 'Improvement',
      dataNotAvailable: 'Data Not Available',
      dataUnavailableMessage: 'Unable to load analysis data. The function may still be deploying or the data may not be available.',
      participantDefault: 'Participant',
      growthPotential: 'Growth Potential',
      businessGrowth: 'Business Growth<br />and Profitability',
      organizationalKnowledge: 'Organizational<br />and Management Knowledge',
      growthMessage: 'Growth potential you see: {potential}% growth in business scope and profitability - with proper organizational and management knowledge implementation!',
      cashFlow: 'Cash Flow',
      improvementNeeded: 'Improvement needed',
      emergencyMode: 'Fire Fighting',
      constantState: 'Constant state',
      unhealthyStress: 'Unhealthy Stress',
      harmingPerformance: 'Harming performance',
      topStrength: 'Your top strength area is {category} - ',
      topWeakness: 'Your most critical weakness is {category} - ',
      mainSymptoms: 'Main symptoms found are the inhibiting or preventing realization of potential and require change are {categories} - immediate intervention needed in these areas',      
      timeToRealize: 'Time to start realizing the potential!',
      nowYouKnow: 'Now you know exactly where the greatest potential awaits you.',
      changeStarts: 'Change starts with proper organizational knowledge - and we know exactly how to do it!',
      keyToSuccess: '💡 Key to Success',
      categoriesBreakdown: 'Category Breakdown by Chart',
      currentState: 'Current State',
      summary: 'Summary',
      analysisConclusion: '🎯 Based on your analysis, there is a clear path to improve performance using existing tools and resources.',
      problemIdentification: '🔍 Problem Source Identification Based on Your Data',
      analysisBasedOn: 'According to your analysis,',
      worstArea: 'the most problematic area is {category}',
      mainProblemSource: 'This is likely the main problem source affecting other areas.',
      improvementImpact: 'Improving this area will positively impact the entire system',
      customSolution: '💡 Solution Tailored for You',
      mainRecommendations: 'Based on your data, the main recommendations are:',
      keyMessage: '🚀 Key Message',
      problemNotCapital: 'The problem is not capital or partners',
      problemIsKnowledge: 'The problem is organizational knowledge',
      solutionFormula: 'Solution = Knowledge + Proper Organizational Implementation',
      analysisCreated: 'Analysis created on:',
      methodology: '',
      copyright: '© 2026 TurnKeyOS.AI — by Samuel Benjamin · Federman Consultants Partner',
      criticalBusinessWarning: '⚠️ Business condition requiring immediate intervention!',
      defaultScore: 'Score:',
      fallbackCategory: 'Management',
    },
    
    // Comment Templates for Detailed Analysis
    commentTemplates: {
      positive: [
        'Excellent response to "{question}" - shows strong {category} capabilities',
        'Outstanding performance on "{question}" demonstrates mastery in {category}',
        'Strong answer to "{question}" indicates solid {category} foundation',
        'Exceptional understanding shown in "{question}" for {category}',
        'Your response to "{question}" reveals excellent {category} skills'
      ],
      improvement: [
        'Consider developing "{question}" further to strengthen {category}',
        'The area addressed by "{question}" needs attention in {category}',
        'Focus on improving "{question}" to enhance {category} performance',
        'Priority development needed for "{question}" in {category}',
        'Growth opportunity identified in "{question}" for {category}'
      ],
      neutral: [
        'Moderate response to "{question}" shows {category} potential',
        'Basic understanding of "{question}" in {category} - room to grow',
        'Good foundation with "{question}" - {category} can be developed',
        '"{question}" shows {category} potential that can be strengthened',
        'Continue building on "{question}" to develop {category} skills'
      ]
    },
    
    // Epok Recommendations
    epokRecommendations: {
      excellent: ['Continue strengthening the subject', 'Continuous improvement', 'Maintain high performance'],
      good: ['Improvements needed', 'Additional development', 'Strengthen existing foundation'],
      average: ['Urgent intervention', 'Significant improvement required', 'Develop from basics'],
      poor: ['Immediate intervention', 'Dramatic change required', 'Professional consultation urgent']
    },
    
    // Category Names
    categoryNames: {
      management: 'Executive',
      organization: 'Organization',
      repeatSales: 'Repeat Sales',
      finance: 'Finance',
      operations: 'Operations',
      qualityControl: 'Quality Control',
      newSales: 'New Sales',
      stress: 'Stress',
      principles: 'Principles',
      helpAcceptance: 'Help',
    },
    
    // General Category Descriptions
    categoryDescriptions: {
      management: 'Measures executive success in achieving expansion, profit and solvency of the company',
      organization: 'Evaluates organizational effectiveness (roles are staffed and performing required tasks within the organization), communication systems, and productive employees. Shows the extent of organizational efficiency with staffed roles that perform what\'s required and communication flow within the organization',
      repeatSales: 'Assesses customer retention capabilities, repeat business effectiveness, and customer relationship management systems',
      finance: 'Examines the level of certainty between planning and reality regarding finances and cash flows',
      operations: 'Examines operational performance. Shows the extent of your satisfaction with three components: quantity, quality, and speed of service or product they want to deliver to customers, versus what actually happens',
      qualityControl: 'Analyzes whether the organization has all the sensors and systems that factually show what\'s really happening. All the statistics and data analysis systems',
      newSales: 'Acquiring new customers or developing new businesses',
      stress: 'To what extent the individual is subject to personal pressures that affect their business activity',
      principles: 'Measures the extent to which the individual\'s management and business principles are sound',
      helpAcceptance: 'Evaluates the extent to which the individual is willing to receive help and openness to learning and improvement'
    },
    
    // Epok Analysis Category Texts
    epokCategories: {
      management: {
        excellent: 'Excellent leadership with strategic planning capabilities and strong execution.',
        good: 'Strong management abilities with good strategic planning. Continue to strengthen leadership and execution.',
        average: 'Basic management capability exists, but improvements are needed',
        poor: 'Company has financial difficulties, bankruptcy risk. Management planning and execution require immediate improvement and change.'
      },
      organization: {
        excellent: 'Excellent organization with advanced structure and processes.',
        good: 'Stable organizational structure with clear roles. Continue to improve processes and efficiency.',
        average: 'Basic organizational structure exists but improvements needed in role definition and work processes that will lead to productive employees',
        poor: 'You feel there is no organization behind you to perform tasks as required. There may be shortage and worse, you may have difficulty implementing management roles'
      },
      repeatSales: {
        excellent: 'Excellent customer satisfaction with strong repeat sales and loyalty programs.',
        good: 'High customer satisfaction with good repeat sales rate. Continue to nurture relationships.',
        average: 'Existing customers generally satisfied but room for improvement in retention and additional sales.',
        poor: 'Likely problems with product/service quality and missing marketing/sales activities - communication with existing customers.'
      },
      finance: {
        excellent: 'Excellent financial stability with advanced planning and control systems.',
        good: 'Stable financial condition with good cash flow control and advanced financial planning.',
        average: 'Reasonable financial condition but improvements needed in cash flow management and financial planning.',
        poor: 'You understand fairly but there are still things you are unsure about, payments, receipts on time as required.'
      },
      operations: {
        excellent: 'Excellent operational processes with high quality and advanced automation.',
        good: 'Efficient operational processes with high quality and meeting deadlines. Continue optimization.',
        average: 'Good basic operations but room for improvements in quality, efficiency and delivery times.',
        poor: 'You are not satisfied with what you provide to customers, the problem could be product quality/quantity/delivery time'
      },
      qualityControl: {
        excellent: 'Advanced quality control system with comprehensive metrics and continuous monitoring.',
        good: 'Advanced quality control system with comprehensive metrics and ongoing monitoring of all activities.',
        average: 'Basic control system exists but expansion and deepening of measurements and monitoring needed',
        poor: 'Relatively sufficient amount of metrics but missing overall activity status - you have "blind spots"'
      },
      newSales: {
        excellent: 'Excellent sales system with skilled team and advanced marketing strategies.',
        good: 'Efficient sales system with skilled team and diverse channels for acquiring new customers.',
        average: 'Basic sales activity exists but improvements needed in marketing and acquisition strategies.',
        poor: 'Huge potential for new customers - likely no marketing activities or no responsible person who knows how to execute this.'
      },
      stress: {
        excellent: 'Healthy stress levels with good work-life balance and wellness maintenance.',
        good: 'Healthy stress level with good work-life balance. Continue to maintain well-being.',
        average: 'Moderate stress level. Improvement needed in work-life balance and pressure management.',
        poor: 'You are on the edge of sleep deprivation and pressure that may already lead to health problems. Pressure comes from high load and lack of stable organization'
      },
      principles: {
        excellent: 'Management and business principles are sound and clear to the individual',
        good: 'Clear management principles well implemented throughout the organization with healthy work culture.',
        average: 'Basic principles exist but better implementation and adaptation to organizational needs required.',
        poor: 'Some management principles you operate by are incorrect and you are frustrated because you lack the tools or knowledge to implement them in the organization.'
      },
      helpAcceptance: {
        excellent: 'High willingness to accept help and trust in others. Strong support system and team confidence.',
        good: 'High willingness to accept help and rely on others. Strong support system and confidence in team.',
        average: 'Reasonable willingness to accept help but still some insecurity in relying on others.',
        poor: 'Person has difficulty accepting help, experienced disappointments with people. Improvement needed in willingness and ability to accept help. Hard to trust others'
      }
    },
  },
  he: {
    // Navigation
    dashboard: 'לוח בקרה',
    sessions: 'מפגשים',
    assessments: 'הערכות',
    organizations: 'ארגונים',
    reports: 'דוחות',
    users: 'משתמשים',
    settings: 'הגדרות',
    testMode: 'מצב בדיקה',
    auditLogs: 'יומני ביקורת',
    
    // Common Actions
    search: 'חיפוש',
    filter: 'סינון',
    export: 'ייצוא',
    import: 'ייבוא',
    create: 'יצירה',
    edit: 'עריכה',
    delete: 'מחיקה',
    save: 'שמירה',
    cancel: 'ביטול',
    confirm: 'אישור',
    refresh: 'רענון',
    download: 'הורדה',
    send: 'שליחה',
    
    // Session Management
    completedSessions: 'מפגשים שהושלמו',
    participantName: 'שם המשתתף',
    participantEmail: 'דוא"ל',
    organization: 'ארגון',
    position: 'תפקיד',
    startedAt: 'התחיל ב',
    completedAt: 'הושלם ב',
    score: 'ציון',
    status: 'סטטוס',
    viewDetails: 'צפייה בפרטים',
    generateChart: 'יצירת תרשים',
    sendResults: 'שליחת תוצאות',
    exportToCSV: 'ייצוא ל-CSV',
    exportToExcel: 'ייצוא לאקסל',
    
    // Assessment Management
    assessmentTitle: 'כותרת הערכה',
    assessmentType: 'סוג',
    language: 'שפה',
    questions: 'שאלות',
    isActive: 'פעיל',
    createdAt: 'נוצר ב',
    updatedAt: 'עודכן ב',
    initializeAssessments: 'אתחול הערכות',
    createAssessment: 'יצירת הערכה',
    toggleStatus: 'שינוי סטטוס',
    preview: 'תצוגה מקדימה',
    
    // Organization Management
    organizationName: 'שם הארגון',
    contactEmail: 'דוא"ל ליצירת קשר',
    totalSessions: 'סה"כ מפגשים',
    lastActivity: 'פעילות אחרונה',
    generateReport: 'יצירת דוח',
    
    // Reports
    dateRange: 'טווח תאריכים',
    from: 'מ',
    to: 'עד',
    averageScore: 'ציון ממוצע',
    categoryScores: 'ציונים לפי קטגוריה',
    participants: 'משתתפים',
    completionTime: 'זמן השלמה',
    scoreDistribution: 'התפלגות ציונים',
    includeCharts: 'כלול תרשימים',
    
    // Data Management
    dataRetention: 'שמירת נתונים',
    retentionDays: 'ימי שמירה',
    cleanupOldData: 'ניקוי נתונים ישנים',
    dryRun: 'הרצת ניסיון',
    deleteTestSessions: 'מחיקת מפגשי בדיקה',
    
    // Messages
    loading: 'טוען...',
    noData: 'אין נתונים זמינים',
    error: 'אירעה שגיאה',
    success: 'הפעולה הושלמה בהצלחה',
    confirmDelete: 'האם אתה בטוח שברצונך למחוק?',
    confirmCleanup: 'האם אתה בטוח שברצונך לנקות נתונים ישנים?',
    emailSent: 'האימייל נשלח בהצלחה',
    exportComplete: 'הייצוא הושלם',
    
    // User Management
    userName: 'שם משתמש',
    userEmail: 'דוא"ל משתמש',
    role: 'תפקיד',
    lastLogin: 'כניסה אחרונה',
    addUser: 'הוספת משתמש',
    editUser: 'עריכת משתמש',
    admin: 'מנהל',
    viewer: 'צופה',
    analyst: 'אנליסט',
    
    // Admin Page Specific
    'admin.dashboard': 'לוח הערכות',
    'admin.subtitle': 'צפייה וניהול של מפגשי הערכה שהושלמו',
    'admin.loading': 'טוען מפגשים...',
    'admin.error': 'נכשל בטעינת מפגשים',
    'admin.filter.organization': 'סינון לפי ארגון',
    'admin.filter.all.organizations': 'כל הארגונים',
    'admin.filter.showing': 'מציג',
    'admin.filter.sessions': 'מפגשים',
    'admin.total.sessions': 'סך הכל מפגשים',
    'admin.epoc.assessments': 'הערכות 360',
    'admin.completed.sessions': 'מפגשים שהושלמו',
    'admin.participant': 'משתתף',
    'admin.assessment': 'הערכה',
    'admin.organization': 'ארגון',
    'admin.completed': 'הושלם',
    'admin.actions': 'פעולות',
    'admin.no.sessions': 'לא נמצאו מפגשים שהושלמו',
    'admin.view.details': 'צפייה בפרטים',
    'admin.session.details': 'פרטי מפגש',
    'admin.participant.info': 'מידע על המשתתף',
    'admin.name': 'שם',
    'admin.email': 'דוא"ל',
    'admin.phone': 'טלפון',
    'admin.position': 'תפקיד',
    'admin.address': 'כתובת',
    'admin.employeeCount': 'מספר עובדים',
    'admin.assessment.results': 'תוצאות הערכה',
    'admin.generating.chart': 'יוצר תרשים...',
    'admin.chart.unavailable': 'תרשים לא זמין',
    'admin.print': ' הדפסה ',
    'admin.printfull': ' הדפסה מלאה ',
    'admin.close': 'סגור',
    'admin.comparison.title': 'פעולות מרובות',
    'admin.comparison.subtitle': 'בחר מספר משתתפים כדי להשוות את תוצאות ההערכה שלהם',
    'admin.comparison.select.all': 'בחר הכל',
    'admin.comparison.generate': 'צור השוואה',
    'admin.comparison.clear': 'נקה בחירה',
    'admin.comparison.participants': 'משתתפים',
    'admin.comparison.generating': 'יוצר השוואה...',
    'admin.comparison.unavailable': 'תרשים השוואה לא זמין',

    // Session Filter
    'admin.filter.completed': 'הושלמו',
    'admin.filter.unfinished': 'לא הושלמו',
    'admin.filter.leads': 'לידים',
    'admin.unfinished.sessions': 'סשנים שלא הושלמו',

    // Quiz Leads
    'admin.quiz.leads': 'לידים מהשאלון',
    'admin.lead.challenge': 'אתגר',
    'admin.lead.urgency': 'דחיפות',
    'admin.lead.status': 'סטטוס',
    'admin.lead.wants360': '360?',
    'admin.lead.date': 'תאריך',
    'admin.no.leads': 'אין לידים עדיין',
    'admin.lead.details': 'פרטי ליד',
    'admin.lead.answers': 'תשובות השאלון',
    'admin.progress': 'סטטוס',
    'admin.status.started': 'התחיל',
    'admin.status.in_progress': 'בתהליך',

    // Edit Participant Dialog
    'edit.participant.dialog.title': 'עריכת פרטי משתתף',
    'edit.participant.dialog.name': 'שם',
    'edit.participant.dialog.email': 'אימייל',
    'edit.participant.dialog.organization': 'ארגון',
    'edit.participant.dialog.position': 'תפקיד',
    'edit.participant.dialog.phone': 'טלפון',
    'edit.participant.dialog.address': 'כתובת',
    'edit.participant.dialog.employeeCount': 'מספר עובדים',
    'edit.participant.dialog.cancel': 'ביטול',
    'edit.participant.dialog.update': 'עדכן',
    'edit.participant.dialog.updating': 'מעדכן...',
    'edit.participant.success': 'פרטי המשתתף עודכנו בהצלחה',
    'edit.participant.error': 'נכשל לעדכן פרטי משתתף',
    
    // Delete Session
    'delete.session.title': 'אישור מחיקה',
    'delete.session.message': 'האם אתה בטוח שברצונך למחוק את הסשן של',
    'delete.session.warning': 'פעולה זו לא ניתנת לביטול. כל נתוני הסשן והתשובות יימחקו לצמיתות.',
    'delete.session.cancel': 'ביטול',
    'delete.session.delete': 'מחק',
    'delete.session.deleting': 'מוחק...',
    'delete.session.success': 'סשן נמחק בהצלחה עבור',
    'delete.session.error': 'נכשל למחוק סשן',
    
    // Bulk Delete
    'bulk.delete.title': 'אישור מחיקה מרובה',
    'bulk.delete.message': 'האם אתה בטוח שברצונך למחוק',
    'bulk.delete.warning': 'פעולה זו לא ניתנת לביטול. כל הסשנים הנבחרים והנתונים שלהם יימחקו לצמיתות.',
    'bulk.delete.cancel': 'ביטול',
    'bulk.delete.delete': 'מחק',
    'bulk.delete.deleting': 'מוחק...',
    'bulk.delete.button': 'מחק נבחרים',
    'bulk.delete.success': 'נמחקו בהצלחה',
    'bulk.delete.error': 'נכשל למחוק סשנים',
    
    // Tooltips
    'tooltip.view.details': 'צפה בפרטים',
    'tooltip.edit.participant': 'ערוך משתתף',
    'tooltip.delete.session': 'מחק סשן',
    
    // Epok Analysis UI Texts
    epokAnalysisUI: {
      title: 'אנליזה מקיפה על בסיס שאלון',
      detailedAnalysisTitle: 'ניתוח מפורט',
      resultsChart: 'תרשים תוצאות',
      subtitle: '',
      strengths: 'חזקות',
      development: 'פיתוח',
      improvement: 'שיפור',
      dataNotAvailable: 'נתונים לא זמינים',
      dataUnavailableMessage: 'לא ניתן לטעון את נתוני הניתוח. ייתכן שהפונקציה עדיין נפרסת או שהנתונים לא זמינים.',
      participantDefault: 'משתתף',
      growthPotential: 'פוטנציאל צמיחה',
      businessGrowth: 'גידול בהיקף<br />הפעילות והרווחיות',
      organizationalKnowledge: 'ידע ארגוני<br />וניהולי נכון',
      growthMessage: 'פוטנציאל הצמיחה שאתה רואה: {potential}% גידול בהיקף הפעילות ושיפור הרווחיות - עם יישום ידע ארגוני וניהולי נכון!',
      cashFlow: 'תזרים מזומנים',
      improvementNeeded: 'נדרש שיפור',
      emergencyMode: 'כיבוי שריפות',
      constantState: 'מצב קבוע',
      unhealthyStress: 'מתח לא בריא',
      harmingPerformance: 'פוגע בביצועים',
      topStrength: 'תחום החוזק המוביל שלך הוא {category} - ',
      topWeakness: 'התחום החלש ביותר שלך הוא {category} - ',
      mainSymptoms: 'הסימפטומים העיקריים שנמצאו הם המעכבים או מונעים את מימוש הפוטנציאל ודורשים שינוי הם {categories} - דרושה התערבות מיידית בתחומים אלה',
      timeToRealize: 'הזמן להתחיל לממש את הפוטנציאל!',
      nowYouKnow: 'עכשיו אתה יודע בדיוק איפה הפוטנציאל הגדול ביותר מחכה לך.',
      changeStarts: 'השינוי מתחיל בידע ארגוני נכון - ואנחנו יודעים בדיוק איך לעשות את זה!',
      keyToSuccess: '💡 המפתח להצלחה',
      categoriesBreakdown: 'פירוט הקטגוריות לפי הגרף',
      currentState: 'מצב נוכחי',
      summary: 'לסיכום',
      analysisConclusion: '🎯 בהתבסס על הניתוח שלך, קיימת דרך ברורה לשפר את הביצועים באמצעות הכלים והאמצעים הקיימים.',
      problemIdentification: '🔍 זיהוי מקור הבעיה על בסיס הנתונים שלך',
      analysisBasedOn: 'על פי הניתוח שלך,',
      worstArea: 'התחום הבעייתי ביותר הוא {category}',
      mainProblemSource: 'זהו כנראה מקור הבעיה המרכזי שמשפיע על שאר התחומים.',
      improvementImpact: 'שיפור תחום זה ישפיע חיובית על כל המערכת',
      customSolution: '💡 הפתרון המותאם לך',
      mainRecommendations: 'בהתבסס על הנתונים שלך, ההמלצות העיקריות הן:',
      keyMessage: '🚀 מסר המפתח',
      problemNotCapital: 'הבעיה אינה בהון או בשותפים',
      problemIsKnowledge: 'הבעיה היא בידע ארגוני',
      solutionFormula: 'הפתרון = ידע + יישום ארגוני נכון',
      analysisCreated: 'ניתוח נוצר בתאריך:',
      methodology: '',
      copyright: '© 2026 TurnKeyOS.AI — מאת סמואל בנימין · פדרמן יועצים שותפים',
      criticalBusinessWarning: '⚠️ מצב עסקי הדורש התערבות מיידית!',
      defaultScore: 'ציון:',
      fallbackCategory: 'ניהול',
    },
    
    // Comment Templates for Detailed Analysis
    commentTemplates: {
      positive: [
        'תגובה מצוינת ל"{question}" - מראה יכולות {category} חזקות',
        'ביצועים יוצאי דופן ב"{question}" מוכיחים שליטה ב{category}',
        'תשובה חזקה ל"{question}" מצביעה על בסיס {category} מוצק',
        'הבנה יוצאת דופן מוצגת ב"{question}" עבור {category}',
        'התגובה שלך ל"{question}" חושפת כישורי {category} מעולים'
      ],
      improvement: [
        'שקול לפתח "{question}" יותר כדי לחזק את {category}',
        'התחום שמטפל בו "{question}" זקוק לתשומת לב ב{category}',
        'התמקד בשיפור "{question}" כדי לשפר ביצועי {category}',
        'פיתוח עדיפות נדרש עבור "{question}" ב{category}',
        'הזדמנות צמיחה זוהתה ב"{question}" עבור {category}'
      ],
      neutral: [
        'תגובה בינונית ל"{question}" מראה פוטנציאל {category}',
        'הבנה בסיסית של "{question}" ב{category} - יש מקום לגדול',
        'בסיס טוב עם "{question}" - {category} ניתן לפתח',
        '"{question}" מראה פוטנציאל {category} שניתן לחזק',
        'המשך לבנות על "{question}" כדי לפתח כישורי {category}'
      ]
    },
    
    // Epok Recommendations
    epokRecommendations: {
      excellent: ['המשך לחזק את הנושא', 'שיפור מתמיד', 'שמירה על ביצועים גבוהים'],
      good: ['שיפורים נדרשים', 'פיתוח נוסף', 'חיזוק הבסיס הקיים'],
      average: ['התערבות דחופה', 'שיפור משמעותי נדרש', 'פיתוח מהבסיס'],
      poor: ['התערבות מיידית', 'שינוי דרמטי נדרש', 'יעוץ מקצועי דחוף']
    },
    
    // Category Names
    categoryNames: {
      management: 'ניהול',
      organization: 'ארגון',
      repeatSales: 'מכירות חוזרות',
      finance: 'כספים',
      operations: 'תפעול',
      qualityControl: 'בקרת איכות',
      newSales: 'מכירות חדשות',
      stress: 'מתח',
      principles: 'עקרונות בסיסיים',
      helpAcceptance: 'עזרה',
    },
    
    // תיאורי קטגוריות כלליים
    categoryDescriptions: {
      management: 'מודד הצלחת המנהל בהשגת התרחבות,רווחים ויכולת פרעון',
      organization: 'מעריך יעילות ארגונית (תפקידים מאוישים ומבצעים את הנדרש בתוך הארגון), מערכות תקשורת ועובדים פרודוקטיביים. מראה עד כמה יש ארגון יעיל עם תפקידים מאוישים שמבצעים את הנדרש וזרימת תקשורת בתוך הארגון',
      repeatSales: 'בוחן יכולות שימור לקוחות, יעילות עסקים חוזרים ומערכות ניהול קשרי לקוחות',
      finance: 'בודק עד כמה קיימת ודאות בין התכנון למציאות בנושא כספים ותזרימים',
      operations: 'בוחן ביצועים תפעוליים. מראה עד כמה אתה מרוצה משלושת המרכיבים: כמות, איכות ומהירות של השירות או המוצר שרוצים לתת ללקוח, לעומת מה שקורה בפועל',
      qualityControl: 'מנתח האם לארגון יש את כל החיישנים והמערכות שמראות עובדתית מה קורה באמת. כל המערכות של סטטיסטיקות וניתוח נתונים',
      newSales: 'רכישת לקוחות חדשים או פיתוח עסקים חדשים',
      stress: 'עד כמה האדם נתון ללחצים אישיים המשפיעים על פעילותו העסקית',
      principles: 'מודד עד כמה העקרונות הניהוליים והעסקיים של האדם נכונים',
      helpAcceptance: 'מעריך עד כמה האדם מוכן לקבל עזרה ופתיחות ללימוד ושיפור'
    },
    
    // Epok Analysis Category Texts
    epokCategories: {
      management: {
        excellent: 'יש מנהיג עם יכולות של לקיחת אחריות בדומה לארגון המיועד למצוינות.',
        good: 'יכולות ניהוליות חזקות עם תכנון אסטרטגי טוב. המשך לחזק את המנהיגות והוצאה לפועל.',
        average: 'יכולת ניהול בסיסית קיימת, אבל דרושים שיפורים',
        poor: 'מצב החברה יש קושי כלכלי סכנה לחדלון פרעון. יכולת ניהול תכנון והוצאה לפועל דורשים שיפור ושינוי מיידי.'
      },
      organization: {
        excellent: 'ארגון מצוין עם מבנה ותהליכים מתקדמים.',
        good: 'מבנה ארגוני יציב עם תפקידים ברורים. המשך לשפר תהליכים ויעילות.',
        average: 'מבנה ארגוני בסיסי קיים אבל דרושים שיפורים בהגדרת תפקידים ובתהליכי עבודה שיביאו לעובדים פרודוקטיביים',
        poor: 'אתה חש שאין מאחוריך ארגון שאמור לבצע את התפקידים כנדרש. יתכן שיש מחסור וחמור מכך יתכן שיש לך קושי בהכנסת תפקידים ניהוליים.'
      },
      repeatSales: {
        excellent: 'שביעות רצון מצוינת של לקוחות עם מכירות חוזרות חזקות ותוכניות נאמנות.',
        good: 'שביעות רצון גבוהה של לקוחות קיימים עם קצב מכירות חוזרות טוב. המשך לטפח את היחסים.',
        average: 'לקוחות קיימים בדרך כלל מרוצים אבל יש מקום לשיפור בשימור ומכירות נוספות.',
        poor: 'כנראה בעיות באיכות המוצר/שירות שמסופק ופעולות שיווקיות/מכירתיות חסרות - תקשורת עם לקוחות קיימים.'
      },
      finance: {
        excellent: 'יציבות פיננסית מצוינת עם מערכות תכנון ובקרה מתקדמות.',
        good: 'מצב כלכלי יציב עם בקרה טובה על תזרים המזומנים ותכנון פיננסי מתקדם.',
        average: 'מצב כלכלי סביר אבל דרושים שיפורים בניהול תזרים מזומנים ותכנון פיננסי.',
        poor: 'אתה די מתמצא אבל עדיין ישנם דברים שאינך בטוח התקיימותם, תשלומים, תקבולים במועד כפי שצריך.'
      },
      operations: {
        excellent: 'תהליכי תפעול מצוינים עם איכות גבוהה ואוטומציה מתקדמת.',
        good: 'תהליכי תפעול יעילים עם איכות גבוהה ועמידה בלוחות זמנים. המשך אופטימיזציה.',
        average: 'תפעול בסיסי טוב אבל יש מקום לשיפורים באיכות, יעילות וזמני אספקה.',
        poor: 'אינך מרוצה ממה שאתה מספק ללקוחותיך, הבעיה יכולה להיות באיכות המוצר/כמות/זמן אספקה.'
      },
      qualityControl: {
        excellent: 'מערכת בקרת איכות מתקדמת עם מדדים מקיפים ומעקב רציף.',
        good: 'מערכת בקרת איכות מתקדמת עם מדדים מקיפים ומעקב שוטף אחר כל הפעילויות.',
        average: 'מערכת בקרה בסיסית קיימת אבל דרושה הרחבה והעמקה של המדידות והמעקב.',
        poor: 'יחסית ישנה כמות מספקת של מדדים אבל חסר מה מצב כלל הפעילויות - יש לך "חורים שחורים" (blind spots)'
      },
      newSales: {
        excellent: 'מערך מכירות מצוין עם צוות מיומן ואסטרטגיות שיווק מתקדמות.',
        good: 'מערך מכירות יעיל עם צוות מיומן וערוצים מגוונים לרכישת לקוחות חדשים.',
        average: 'פעילות מכירות בסיסית קיימת אבל דרושים שיפורים בשיווק ובאסטרטגיות רכישה.',
        poor: 'יש פוטנציאל עצום ללקוחות חדשים - כנראה אין פעולות שיווק או אין גורם שאחראי ויודע לבצע זאת.'
      },
      stress: {
        excellent: 'רמות מתח בריאות עם איזון טוב עבודה-חיים ושמירה על רווחה.',
        good: 'רמת מתח בריאה עם איזון טוב בין עבודה לחיים האישיים. המשך לשמור על הרווחה.',
        average: 'רמת מתח בינונית. דרוש שיפור באיזון עבודה-חיים ובניהול לחצים.',
        poor: 'אתה על הגבול של מדיר שינה ולחץ שכבר עשוי להביא לבעיות בריאותיות. הלחץ נובע מעומס גבוה ומחסור בארגון יציב.'
      },
      principles: {
        excellent: 'העקרונות הניהוליים והעסקיים נכונים וברורים לאדם',
        good: 'עקרונות ניהוליים ברורים ומיושמים היטב בכל הארגון עם תרבות עבודה בריאה.',
        average: 'עקרונות בסיסיים קיימים אבל דרושה הטמעה טובה יותר והתאמה לצרכי הארגון.',
        poor: 'חלק מהעקרונות הניהוליים שאתה פועל על פיהם אינם נכונים ואתה מתוסכל כי אין לך את הכלים או הידע להטמיע אותם בארגון.'
      },
      helpAcceptance: {
        excellent: 'נכונות גבוהה לקבל עזרה ולבטוח באחרים. מערכת תמיכה חזקה ובטחון בצוות.',
        good: 'נכונות גבוהה לקבל עזרה ולסמוך על אחרים. מערכת תמיכה חזקה וביטחון בצוות.',
        average: 'נכונות סבירה לקבל עזרה אבל עדיין קיים חוסר ביטחון מסוים בהסתמכות על אחרים.',
        poor: 'האדם קשה לו לקבל עזרה, עבר אכזבות עם אנשים. דרוש שיפור בנכונות וביכולת לקבל עזרה. קשה לסמוך על אחרים.'
      }
    },
  },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  direction: 'ltr' | 'rtl';
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Get saved language from localStorage or default to Hebrew
    const saved = localStorage.getItem('adminLanguage');
    return (saved as Language) || 'he';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('adminLanguage', lang);
    // Update document direction
    document.documentElement.dir = lang === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  };

  useEffect(() => {
    // Set initial direction
    document.documentElement.dir = language === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations[language],
    direction: language === 'he' ? 'rtl' : 'ltr',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Helper function to format dates according to language
export const formatDate = (date: Date | string, language: Language): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const locale = language === 'he' ? 'he-IL' : 'en-US';
  return d.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Helper function to format numbers according to language
export const formatNumber = (num: number, language: Language): string => {
  const locale = language === 'he' ? 'he-IL' : 'en-US';
  return num.toLocaleString(locale);
};